import Form from '@rjsf/mui';
import { useCallback, useMemo, useState } from 'react';
import validator from 'utils/json-form/validator';
import { Box, Button, Grid } from '@mui/material';
import { theme } from 'theme';
import { palette } from 'theme/palette';
import { transformErrors } from 'utils/json-form/formErrorUtils';
import { getWidgets } from 'utils/json-form/widgets';
import { uiSchema } from 'utils/json-form/formDataUtils';
import { jbColors } from 'theme/jbColors';
import { DescriptionFieldProps, RJSFSchema } from '@rjsf/utils';
import { StepMetadata } from 'pco-product-service-internal-consumer';
import { Task } from 'pco-product-service-internal-consumer';

interface ProductApprovalProps {
  attributes: Task | (StepMetadata & Partial<{ content: string; schema: string }>) | undefined;
  handleSubmit?: (formData: { [key: string]: unknown }) => void;
  handleCancel: () => void;
  hasHandleCancel?: boolean;
  isReadOnly?: boolean;
}

const ProductApproval = ({
  attributes,
  handleCancel,
  handleSubmit,
  hasHandleCancel = true,
  isReadOnly,
}: ProductApprovalProps) => {
  const initialFormData = attributes?.content ? JSON.parse(attributes.content) : {};
  const schema = useMemo<RJSFSchema>(() => {
    return attributes?.schema ? JSON.parse(attributes.schema) : ({} as RJSFSchema);
  }, [attributes]);
  const [formData, setFormData] = useState(initialFormData);
  const isFormValid = useMemo(() => validator.isValid(schema, formData, schema), [schema, formData]);

  const onFormChange = useCallback((formData: Record<string, unknown>) => {
    setFormData(formData);
  }, []);

  function DescriptionFieldTemplate(props: DescriptionFieldProps) {
    const { description } = props;

    return <div dangerouslySetInnerHTML={{ __html: description.toString() }} />;
  }

  const styles = {
    '& .MuiFormLabel-root': {
      fontSize: '13px',
      color: palette.text.primary,
    },
    '& .MuiInputLabel-animated': {
      color: palette.black,
    },
    '& label': {
      color: 'black',
      fontSize: '13px',
    },
    '& .MuiTypography-subtitle2': {
      fontWeight: 'normal',
      fontSize: '13px',
      marginLeft: '0.5em',
    },
    '& .MuiTypography-root': {
      fontSize: '13px',
      color: 'black',
      fontWeight: 'normal',
    },
    '& .MuiFormGroup-root': {
      display: 'flex',
      flexDirection: 'row',
    },
    '& .MuiCheckbox-root': {
      backgroundColor: 'white',
    },
    '& .MuiFormControl-root': {
      mb: theme.spacing(1),
    },
    '& .MuiFormControlLabel-root': {
      marginLeft: 0,
      padding: '0.2rem',
    },
    '& .MuiFormControlLabel-root span': {
      whiteSpace: 'nowrap',
    },
  };

  return (
    <Box data-testid="product-approval" sx={styles}>
      {schema && (
        <Form
          schema={schema}
          validator={validator}
          omitExtraData={true}
          uiSchema={uiSchema}
          formData={formData}
          liveOmit={true}
          onChange={(e) => onFormChange(e.formData)}
          disabled={isReadOnly}
          widgets={getWidgets()}
          liveValidate
          transformErrors={transformErrors}
          showErrorList={false}
          templates={{ DescriptionFieldTemplate }}
        >
          <Grid
            container
            sx={{
              justifyContent: hasHandleCancel ? 'space-between' : 'flex-end',
            }}
          >
            {hasHandleCancel && (
              <Button
                id="product-approval-cancel"
                data-testid="product-approval-cancel-id"
                onClick={handleCancel}
                variant="contained"
                sx={{
                  color: jbColors.core.reflexBlue[100],
                  backgroundColor: 'white',
                  border: `1px solid ${jbColors.core.reflexBlue[100]}`,
                  '&:hover': {
                    backgroundColor: jbColors.core.reflexBlue[100],
                    color: 'white',
                  },
                }}
              >
                Cancel
              </Button>
            )}
            {!isReadOnly ? (
              <Button
                sx={{
                  backgroundColor: jbColors.core.reflexBlue[100],
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'white',
                    color: jbColors.core.reflexBlue[100],
                  },
                  '&:disabled': {
                    border: `1px solid ${jbColors.support.solidGrayIcon[10]}`,
                    backgroundColor: jbColors.core.stone[10],
                    color: jbColors.support.solidGrayIcon[10],
                  },
                }}
                id="product-approval-submit"
                data-testid="product-approval-submit-id"
                onClick={() => handleSubmit?.(formData)}
                variant="contained"
                disabled={!isFormValid}
              >
                Submit
              </Button>
            ) : null}
          </Grid>
        </Form>
      )}
    </Box>
  );
};

export default ProductApproval;
